import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Link, useParams } from 'react-router-dom';
import { WarningCircle } from '@phosphor-icons/react';
import CScoreChart from '../components/CScoreChart';
import ScoreBadge from '../components/ScoreBadge';
import ScoreCell from '../components/ScoreCell';
import HoverCard from '../components/HoverCard';
import ScoreBreakdown from '../components/ScoreBreakdown';
import DischargeBreakdown from '../components/DischargeBreakdown';
import MonthRangePicker from '../components/MonthRangePicker';
import TrendPanel from '../components/TrendPanel';
import Avatar from '../components/Avatar';
import Breadcrumb from '../components/Breadcrumb';
import { api } from '../lib/api';
import { useScoring, isRedZone } from '../lib/scoring';
import { findMissingData, MISSING_FIELD_LABELS } from '../lib/missing-data';
import { SkeletonChart, SkeletonTable, SkeletonHeader, SkeletonBreadcrumb } from '../components/Skeleton';

const MONTH_NAMES = [
  '', 'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'
];

/**
 * The in-progress month has a live caseload but no discharge or attendance data
 * yet, so it carries no CScore. Showing a red 0 there would read as a failing
 * clinician rather than a month that hasn't closed.
 */
function RecordScore({ record, weights, threshold }) {
  if (record.cscore == null) {
    return (
      <span
        className="inline-flex items-center justify-center rounded-full font-medium bg-gray-100 text-gray-500 min-w-[48px] h-7 text-xs px-2.5"
        title="Awaiting this month's discharge and attendance data"
      >
        Pending
      </span>
    );
  }
  return (
    <HoverCard panel={<ScoreBreakdown breakdown={record} score={record.cscore} weights={weights} title="This month" />}>
      <ScoreBadge score={record.cscore} flagged={isRedZone(record.cscore, threshold)} />
    </HoverCard>
  );
}

/**
 * The discharge count, with its type breakdown on hover. The number itself
 * stays editable — the hover explains what KIPU reported, the cell underneath
 * is still how a human corrects it.
 */
function DischargeCell({ record, clinicId, onRecordUpdate }) {
  return (
    <HoverCard panel={<DischargeBreakdown record={record} />}>
      <EditableCell value={record.discharges} field="discharges" recordId={record.id} clinicId={clinicId} onRecordUpdate={onRecordUpdate} />
    </HoverCard>
  );
}

/**
 * Caseload is auto-calculated from the daily KIPU caseload report, so the cell
 * says where its number came from — and warns that typing over it pins the
 * value and stops future daily syncs from updating it.
 */
function CaseloadCell({ record, clinicId, onRecordUpdate }) {
  const daily = record.daily_caseload;
  const pinned = (record.manual_overrides || []).includes('caseload');

  const hint = pinned
    ? 'Manually set. Daily caseload syncs will no longer update this value.'
    : daily
      ? `Auto-calculated: ${daily.total} cases over ${daily.days} report day${daily.days === 1 ? '' : 's'} (${daily.first_date} to ${daily.last_date})`
      : null;

  return (
    <div className="inline-block" title={hint || undefined}>
      <EditableCell value={record.caseload} field="caseload" recordId={record.id} clinicId={clinicId} onRecordUpdate={onRecordUpdate} />
      {pinned ? (
        <span className="block text-[10px] text-amber-600 px-2 leading-tight">Manual</span>
      ) : daily ? (
        <span className="block text-[10px] text-gray-400 px-2 leading-tight">{daily.total} / {daily.days}d</span>
      ) : null}
    </div>
  );
}

/**
 * Attendance is auto-calculated from the daily KIPU group attendance report,
 * so, like Caseload, the cell says where its number came from and warns that
 * typing over it pins the value.
 */
function AttendanceCell({ record, clinicId, onRecordUpdate }) {
  const daily = record.daily_attendance;
  const computed = daily && daily.pct != null ? daily : null;
  const pinned = (record.manual_overrides || []).includes('attendance_pct');
  const days = n => Number(n.toFixed(1));

  const unknown = daily && daily.unknown_program_patients
    ? ` ${daily.unknown_program_patients} patient${daily.unknown_program_patients === 1 ? '' : 's'} left out (program unknown).`
    : '';
  const hint = pinned
    ? `Manually set. Daily attendance syncs will no longer update this value.${computed ? ` Calculated: ${(computed.pct * 100).toFixed(1)}%.` : ''}`
    : computed
      ? `Auto-calculated: ${days(computed.attended_days)} of ${days(computed.expected_days)} expected patient-days attended, ${computed.patients} patient${computed.patients === 1 ? '' : 's'} (${computed.first_date} to ${computed.last_date}).${unknown}`
      : null;

  return (
    <div className="inline-block" title={hint || undefined}>
      <EditableCell value={record.attendance_pct} field="attendance_pct" recordId={record.id} clinicId={clinicId} onRecordUpdate={onRecordUpdate} />
      {pinned ? (
        <span className="block text-[10px] text-amber-600 px-2 leading-tight">Manual</span>
      ) : computed ? (
        <span className="block text-[10px] text-gray-400 px-2 leading-tight">{days(computed.attended_days)} / {days(computed.expected_days)}d</span>
      ) : null}
    </div>
  );
}

function EditableCell({ value, field, recordId, clinicId, onRecordUpdate }) {
  const [editing, setEditing] = useState(false);
  const [displayVal, setDisplayVal] = useState(value);
  const [localValue, setLocalValue] = useState(value ?? '');

  // Keep the displayed value in sync with server truth (e.g. after a recalc
  // reload) so the cell never drifts from what was actually persisted.
  useEffect(() => {
    if (!editing) setDisplayVal(value);
  }, [value, editing]);

  const displayValue = field === 'attendance_pct'
    ? (displayVal != null ? (displayVal * 100).toFixed(2) + '%' : '-')
    : (displayVal != null && displayVal !== '' ? displayVal : '-');

  const handleBlur = async () => {
    setEditing(false);
    let parsed;
    if (field === 'attendance_pct') {
      parsed = parseFloat(localValue) / 100;
      if (isNaN(parsed)) return;
    } else {
      parsed = parseFloat(localValue);
      if (isNaN(parsed)) return;
    }
    if (parsed === displayVal) return;
    setDisplayVal(parsed);
    try {
      const updated = await api.clinics.updateRecord(clinicId, recordId, { [field]: parsed });
      if (onRecordUpdate) onRecordUpdate(recordId, updated);
    } catch (err) {
      console.error('Save failed:', err);
      setDisplayVal(value); // revert on error
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') e.target.blur();
    if (e.key === 'Escape') { setEditing(false); setLocalValue(value ?? ''); }
  };

  return (
    <div className="inline-block min-w-[60px]">
      {editing ? (
        <input
          type="number"
          step={field === 'attendance_pct' ? '0.01' : '1'}
          value={localValue}
          onChange={(e) => setLocalValue(e.target.value)}
          onBlur={handleBlur}
          onKeyDown={handleKeyDown}
          autoFocus
          className="w-full border border-primary rounded px-2 py-1 text-sm focus:outline-none focus:ring-1 focus:ring-primary appearance-none [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
          style={{ MozAppearance: 'textfield' }}
        />
      ) : (
        <span
          className={`cursor-pointer hover:bg-primary-10 px-2 py-1 rounded transition-colors text-sm block ${
            (field === 'caseload' || field === 'attendance_pct') && (displayVal == null || displayVal === 0) ? 'text-destructive' : ''
          }`}
          onClick={() => {
            setLocalValue(field === 'attendance_pct' && value != null ? (value * 100).toFixed(2) : (value ?? ''));
            setEditing(true);
          }}
        >
          {displayValue}
        </span>
      )}
    </div>
  );
}

/** "Attendance, Length of stay" for a list of missing field keys. */
function fieldList(fields) {
  return fields.map(f => MISSING_FIELD_LABELS[f]).join(', ');
}

/**
 * Per-row flag for a clinician-month with a CScore input missing. The hover
 * names the fields, the same way the score and discharge hovers explain theirs.
 */
function MissingBadge({ gap }) {
  if (!gap) return null;
  return (
    <HoverCard
      width={240}
      panel={(
        <div>
          <h4 className="text-sm font-bold text-text mb-1">Missing this month</h4>
          <p className="text-xs text-gray-600 leading-tight">{fieldList(gap.fields)}</p>
        </div>
      )}
    >
      <span className="inline-flex items-center gap-1 text-xs font-medium text-amber-700 bg-amber-50 rounded-full px-2 py-0.5 whitespace-nowrap">
        <WarningCircle size={12} weight="bold" />
        Missing data
      </span>
    </HoverCard>
  );
}

/**
 * Month heads-up: which included clinicians are missing a CScore
 * input. One line per field, expandable to the names behind it.
 */
function MissingDataBanner({ gaps, clinicId, clinicName }) {
  const [open, setOpen] = useState(false);
  if (gaps.length === 0) return null;

  const counts = Object.keys(MISSING_FIELD_LABELS)
    .map(field => ({ field, count: gaps.filter(g => g.fields.includes(field)).length }))
    .filter(c => c.count > 0);

  return (
    <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 mb-4">
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-start gap-2 text-sm text-amber-800">
          <WarningCircle size={18} weight="bold" className="shrink-0 mt-px" />
          <p>
            {counts.map(({ field, count }, i) => (
              <span key={field}>
                {i > 0 && ' · '}
                <span className="font-medium">{MISSING_FIELD_LABELS[field]}</span> missing for {count} clinician{count === 1 ? '' : 's'}
              </span>
            ))}
          </p>
        </div>
        <button
          type="button"
          aria-expanded={open}
          onClick={() => setOpen(o => !o)}
          className="shrink-0 text-xs font-medium text-amber-800 hover:underline"
        >
          {open ? 'Hide' : 'Show clinicians'}
        </button>
      </div>
      {open && (
        <ul className="mt-3 pt-3 border-t border-amber-200 space-y-1.5">
          {gaps.map(gap => (
            <li key={gap.record_id} className="flex flex-wrap items-baseline gap-x-3 text-sm">
              <Link to={`/clinicians/${gap.clinician_id}?clinicId=${clinicId}&clinicName=${encodeURIComponent(clinicName)}`} className="text-primary font-medium hover:underline">
                {gap.name}
              </Link>
              <span className="text-xs text-amber-800">{fieldList(gap.fields)}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function IncludeToggle({ included, onToggle }) {
  return (
    <div className="flex items-center justify-center gap-2">
      <button
        type="button"
        role="switch"
        aria-checked={included}
        onClick={onToggle}
        className={`relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-offset-1 ${
          included ? 'bg-green-500 focus:ring-green-400' : 'bg-red-500 focus:ring-red-400'
        }`}
      >
        <span
          className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition-transform ${
            included ? 'translate-x-5' : 'translate-x-1'
          }`}
        />
      </button>
      <span className={`text-xs font-medium whitespace-nowrap ${included ? 'text-green-600' : 'text-red-600'}`}>
        {included ? 'Included' : 'Not included'}
      </span>
    </div>
  );
}

/**
 * Every clinician's metrics rolled up across the selected months.
 *
 * The score in each row is NOT the average of that clinician's monthly
 * CScores — the raw metrics are summed and averaged across the span and then
 * scored once, so a range reads the way a single long reporting period would.
 */
/** "2026-01" -> "January 2026", for headings the client reads. */
function formatMonthLabel(value) {
  const [year, month] = String(value || '').split('-');
  const name = MONTH_NAMES[Number(month)];
  return name ? `${name} ${year}` : value;
}

function RangeTable({ data, loading, clinicId, clinicName, weights, threshold }) {
  if (loading) {
    return <div className="bg-white rounded-xl border border-[#E6E6E6] p-6 text-center text-gray-400 text-sm">Loading range...</div>;
  }
  if (!data || data.clinicians.length === 0) {
    return <div className="bg-white rounded-xl border border-[#E6E6E6] p-6 text-sm text-gray-500">No records in this range.</div>;
  }

  const pct = (v) => (v != null ? `${(Number(v) * 100).toFixed(1)}%` : '-');
  const num = (v, d = 1) => (v != null ? Number(v).toFixed(d) : '-');
  const flagged = data.clinicians.filter(c => c.include_in_cscore && isRedZone(c.cscore, threshold));

  return (
    <div className="bg-white rounded-xl border border-[#E6E6E6] p-4 md:p-6">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-4">
        <div>
          <h3 className="text-xl font-bold text-text">
            {data.from === data.to
              ? formatMonthLabel(data.from)
              : `${formatMonthLabel(data.from)} to ${formatMonthLabel(data.to)}`}
          </h3>
          <p className="text-sm text-gray-500 mt-0.5">
            {data.month_span} month{data.month_span === 1 ? '' : 's'} · {data.clinicians.length} clinician{data.clinicians.length === 1 ? '' : 's'}
            {flagged.length > 0 && <span className="text-destructive font-medium"> · {flagged.length} in the red zone</span>}
          </p>
        </div>
        {data.avg_cscore != null && (
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium text-gray-600">Clinic average</span>
            <ScoreBadge score={data.avg_cscore} size="lg" flagged={isRedZone(data.avg_cscore, threshold)} />
          </div>
        )}
      </div>

      <table className="w-full hidden md:table">
        <thead>
          <tr className="border-b border-gray-200">
            <th className="text-left py-3 text-sm font-semibold text-gray-700">Name</th>
            <th className="text-left py-3 text-sm font-semibold text-gray-700 w-28">CScore</th>
            <th className="text-left py-3 text-sm font-semibold text-gray-700 w-28">Caseload</th>
            <th className="text-left py-3 text-sm font-semibold text-gray-700 w-28">Discharges</th>
            <th className="text-left py-3 text-sm font-semibold text-gray-700 w-32">Length of Stay</th>
            <th className="text-left py-3 text-sm font-semibold text-gray-700 w-28">Attendance</th>
          </tr>
        </thead>
        <tbody>
          {data.clinicians.map((row) => (
            <tr key={row.clinician_id} className={`border-b border-gray-50 last:border-0 ${row.include_in_cscore ? '' : 'opacity-50'}`}>
              <td className="py-4">
                <Link to={`/clinicians/${row.clinician_id}?clinicId=${clinicId}&clinicName=${encodeURIComponent(clinicName)}`} className="text-primary font-medium hover:underline text-sm">
                  {row.clinician_name}
                </Link>
                <p className="text-xs text-gray-400 mt-0.5">{row.month_count} month{row.month_count === 1 ? '' : 's'} of data</p>
              </td>
              <td className="py-4">
                {row.cscore == null ? (
                  <span className="text-xs text-gray-400">Pending</span>
                ) : (
                  <ScoreCell score={row.cscore} breakdown={row} weights={weights} threshold={threshold} title="Across this range" />
                )}
              </td>
              <td className="py-4 text-sm">{num(row.caseload)}</td>
              <td className="py-4 text-sm">{num(row.discharges, 0)}</td>
              <td className="py-4 text-sm">{num(row.length_of_stay, 0)}</td>
              <td className="py-4 text-sm">{pct(row.attendance_pct)}</td>
            </tr>
          ))}
        </tbody>
      </table>

      <div className="md:hidden space-y-3">
        {data.clinicians.map((row) => (
          <div key={row.clinician_id} className={`border border-[#E6E6E6] rounded-lg p-3 ${row.include_in_cscore ? '' : 'opacity-50'}`}>
            <div className="flex items-center justify-between gap-2 mb-2">
              <Link to={`/clinicians/${row.clinician_id}?clinicId=${clinicId}&clinicName=${encodeURIComponent(clinicName)}`} className="text-primary font-medium hover:underline text-sm truncate">
                {row.clinician_name}
              </Link>
              {row.cscore == null
                ? <span className="text-xs text-gray-400 shrink-0">Pending</span>
                : <ScoreCell score={row.cscore} breakdown={row} weights={weights} threshold={threshold} title="Across this range" />}
            </div>
            <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-sm">
              <div className="flex justify-between"><span className="text-gray-500">Caseload</span><span>{num(row.caseload)}</span></div>
              <div className="flex justify-between"><span className="text-gray-500">Discharges</span><span>{num(row.discharges, 0)}</span></div>
              <div className="flex justify-between"><span className="text-gray-500">LOS</span><span>{num(row.length_of_stay, 0)}</span></div>
              <div className="flex justify-between"><span className="text-gray-500">Attendance</span><span>{pct(row.attendance_pct)}</span></div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

/** "YYYY-MM" for a Date, which is what the range endpoints expect. */
function toMonthValue(date) {
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
}

/** Default span: the last six months up to and including this one. */
function defaultRange() {
  const now = new Date();
  const start = new Date(now.getFullYear(), now.getMonth() - 5, 1);
  return { from: toMonthValue(start), to: toMonthValue(now) };
}

const VIEWS = [
  { key: 'overview', label: 'Overview' },
  { key: 'range', label: 'Date range' },
  { key: 'trends', label: 'Trends' },
];

export default function ClinicDetail() {
  const { clinicId } = useParams();
  const { threshold, weights } = useScoring();
  const [view, setView] = useState('overview');
  const [range, setRange] = useState(defaultRange);
  const [rangeData, setRangeData] = useState(null);
  const [trendData, setTrendData] = useState(null);
  const [rangeLoading, setRangeLoading] = useState(false);
  const [rangeError, setRangeError] = useState(null);
  const [clinic, setClinic] = useState(null);
  const [clinicians, setClinicians] = useState([]);
  const [chartData, setChartData] = useState([]);
  const [selectedMonth, setSelectedMonth] = useState(null);
  const [selectedYear, setSelectedYear] = useState(null);
  const [monthRecords, setMonthRecords] = useState([]);
  const [loadingRecords, setLoadingRecords] = useState(false);
  const [recalculating, setRecalculating] = useState(false);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');

  useEffect(() => {
    Promise.all([
      api.clinics.get(clinicId),
      api.clinics.chart(clinicId),
      api.clinics.clinicians(clinicId),
    ])
      .then(([clinicData, chart, cliniciansList]) => {
        setClinic(clinicData);
        setChartData(chart);
        setClinicians(cliniciansList);
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [clinicId]);

  const loadMonthRecords = useCallback(() => {
    if (!selectedMonth || !selectedYear) return;
    setLoadingRecords(true);
    api.clinics.records(clinicId, selectedMonth, selectedYear)
      .then(setMonthRecords)
      .catch(console.error)
      .finally(() => setLoadingRecords(false));
  }, [clinicId, selectedMonth, selectedYear]);

  useEffect(() => {
    loadMonthRecords();
  }, [loadMonthRecords]);

  // Range and trend share one span, so switching between those two tabs
  // reuses whichever call has already been made for it.
  useEffect(() => {
    if (view === 'overview') return;

    let active = true;
    setRangeLoading(true);
    setRangeError(null);

    const request = view === 'range'
      ? api.clinics.range(clinicId, range.from, range.to).then(data => ({ rangeData: data }))
      : api.clinics.trend(clinicId, range.from, range.to).then(data => ({ trendData: data }));

    request
      .then(result => {
        if (!active) return;
        if (result.rangeData) setRangeData(result.rangeData);
        else setTrendData(result.trendData);
      })
      .catch(err => {
        if (!active) return;
        console.error(err);
        setRangeError('Could not load data for this range.');
      })
      .finally(() => { if (active) setRangeLoading(false); });

    return () => { active = false; };
  }, [view, clinicId, range.from, range.to]);

  // Recomputed from local state, so a typed-in value or an include toggle
  // clears its flag straight away.
  const missingGaps = useMemo(
    () => (selectedMonth && selectedYear
      ? findMissingData(monthRecords, clinicians, { month: selectedMonth, year: selectedYear })
      : []),
    [monthRecords, clinicians, selectedMonth, selectedYear]
  );
  const gapByRecord = useMemo(() => new Map(missingGaps.map(g => [g.record_id, g])), [missingGaps]);

  const handleRecordUpdate = useCallback((recordId, updatedRecord) => {
    setMonthRecords(prev => prev.map(r => r.id === recordId ? { ...r, ...updatedRecord } : r));
  }, []);

  const handleToggleInclude = async (clinician) => {
    const next = !(clinician.include_in_cscore !== false);
    // Optimistically flip the toggle, then recompute the clinic CScore chart.
    setClinicians(prev => prev.map(c => c.id === clinician.id ? { ...c, include_in_cscore: next } : c));
    try {
      await api.clinics.updateClinician(clinicId, clinician.id, { include_in_cscore: next });
      const chart = await api.clinics.chart(clinicId);
      setChartData(chart);
    } catch (err) {
      console.error('Toggle include failed:', err);
      setClinicians(prev => prev.map(c => c.id === clinician.id ? { ...c, include_in_cscore: !next } : c));
    }
  };

  const handlePointClick = (month, year) => {
    setSelectedMonth(month);
    setSelectedYear(year);
  };

  const handleRecalculate = async () => {
    setRecalculating(true);
    try {
      await api.clinics.recalculate(clinicId, selectedMonth, selectedYear);
      // Reload records and chart
      await loadMonthRecords();
      const chart = await api.clinics.chart(clinicId);
      setChartData(chart);
    } catch (err) {
      console.error('Recalculate failed:', err);
    }
    setRecalculating(false);
  };

  if (loading || !clinic) return (
    <div>
      <SkeletonBreadcrumb />
      <SkeletonHeader />
      <SkeletonChart />
      <div className="mt-6"><SkeletonTable rows={5} /></div>
    </div>
  );

  return (
    <div>
      <Breadcrumb items={[
        { label: 'Home', to: '/' },
        { label: 'Clinics', to: '/clinics' },
        { label: clinic.name },
      ]} />

      <h1 className="text-2xl md:text-[32px] font-semibold text-text mb-4">{clinic.name}</h1>

      {/* View switcher */}
      <div className="flex gap-1 border-b border-gray-200 mb-6" role="tablist">
        {VIEWS.map(({ key, label }) => (
          <button
            key={key}
            role="tab"
            aria-selected={view === key}
            onClick={() => setView(key)}
            className={`px-4 py-2.5 text-sm font-medium border-b-2 -mb-px transition-colors ${
              view === key
                ? 'border-primary text-primary'
                : 'border-transparent text-gray-500 hover:text-text'
            }`}
          >
            {label}
          </button>
        ))}
      </div>

      {view === 'overview' && chartData.length > 0 && (
        <CScoreChart
          data={chartData}
          onPointClick={handlePointClick}
          selectedMonth={selectedMonth}
          selectedYear={selectedYear}
        />
      )}

      {view !== 'overview' && (
        <div className="bg-white rounded-xl border border-[#E6E6E6] p-4 md:p-6 mb-6">
          <MonthRangePicker
            from={range.from}
            to={range.to}
            max={toMonthValue(new Date())}
            disabled={rangeLoading}
            onChange={setRange}
          />
          <p className="text-xs text-gray-400 mt-3 leading-tight">
            KIPU reports discharges, attendance and length of stay once per month,
            so ranges are selected in whole months.
          </p>
        </div>
      )}

      {view !== 'overview' && rangeError && (
        <div className="bg-white rounded-xl border border-[#E6E6E6] p-6 text-sm text-destructive">{rangeError}</div>
      )}

      {view === 'range' && !rangeError && (
        <RangeTable data={rangeData} loading={rangeLoading} clinicId={clinicId} clinicName={clinic.name} weights={weights} threshold={threshold} />
      )}

      {view === 'trends' && !rangeError && (
        <div className="bg-white rounded-xl border border-[#E6E6E6] p-4 md:p-6">
          <h3 className="text-xl font-bold text-text mb-1">Where each clinician is heading</h3>
          <p className="text-sm text-gray-500 mb-4">Comparing the first and last scored month in the selected range</p>
          <TrendPanel series={trendData} clinicId={clinicId} clinicName={clinic.name} loading={rangeLoading} />
        </div>
      )}

      {/* Default: All Clinicians with avg CScore */}
      {view === 'overview' && !selectedMonth && (
        <div className="bg-white rounded-xl border border-[#E6E6E6] p-4 md:p-6 mt-6">
          <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-3 mb-4">
            <h3 className="text-xl font-bold text-text">All Clinicians</h3>
            {clinicians.length > 0 && (
              <input
                type="text"
                placeholder="Search"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="border border-gray-200 rounded-lg px-4 py-2 text-sm w-full sm:w-48 focus:outline-none focus:ring-2 focus:ring-primary-30 focus:border-primary-30"
              />
            )}
          </div>

          {clinicians.length === 0 ? (
            <p className="text-sm text-gray-500 py-4">No clinicians for this clinic</p>
          ) : (
            <>
              <table className="w-full hidden sm:table">
                <thead>
                  <tr className="border-b border-gray-200">
                    <th className="text-left py-3 text-sm font-semibold text-gray-700">Name</th>
                    <th className="text-center py-3 text-sm font-semibold text-gray-700 w-48">Average CScore</th>
                    <th className="text-center py-3 text-sm font-semibold text-gray-700 w-56">In CScore</th>
                  </tr>
                </thead>
                <tbody>
                  {clinicians
                    .filter(c => c.full_name.toLowerCase().includes(search.toLowerCase()))
                    .map((clinician) => {
                      const included = clinician.include_in_cscore !== false;
                      const isActive = clinician.active !== false;
                      return (
                    <tr key={clinician.id} className="border-b border-gray-50 last:border-0">
                      <td className={`py-4 transition-opacity ${included ? '' : 'opacity-50'}`}>
                        <div className="flex items-center gap-3">
                          <Avatar src={clinician.image_url} name={clinician.full_name} size="md" />
                          <div>
                            <div className="flex items-center gap-2">
                              <Link to={`/clinicians/${clinician.id}?clinicId=${clinicId}&clinicName=${encodeURIComponent(clinic.name)}`} className="text-primary font-medium hover:underline text-sm">{clinician.full_name}</Link>
                              {!isActive && <span className="text-xs font-medium text-gray-500 bg-gray-100 rounded-full px-2 py-0.5">Inactive</span>}
                            </div>
                            <p className="text-xs text-gray-500 mt-0.5">Clinician ID: {clinician.kipu_clinician_id}</p>
                          </div>
                        </div>
                      </td>
                      <td className={`py-4 text-center transition-opacity ${included ? '' : 'opacity-50'}`}>
                        <ScoreCell
                          score={clinician.avg_cscore}
                          breakdown={clinician.breakdown}
                          weights={weights}
                          threshold={threshold}
                          title={`${clinician.full_name} average`}
                        />
                      </td>
                      <td className="py-4"><IncludeToggle included={included} onToggle={() => handleToggleInclude(clinician)} /></td>
                    </tr>
                      );
                    })}
                </tbody>
              </table>
              <div className="sm:hidden">
                {clinicians
                  .filter(c => c.full_name.toLowerCase().includes(search.toLowerCase()))
                  .map((clinician, idx, arr) => {
                    const included = clinician.include_in_cscore !== false;
                    const isActive = clinician.active !== false;
                    return (
                  <div key={clinician.id} className={`flex items-center justify-between gap-3 py-3 ${idx !== arr.length - 1 ? 'border-b border-gray-100' : ''}`}>
                    <div className={`flex items-center gap-3 min-w-0 transition-opacity ${included ? '' : 'opacity-50'}`}>
                      <Avatar src={clinician.image_url} name={clinician.full_name} size="md" />
                      <div className="min-w-0">
                        <Link to={`/clinicians/${clinician.id}?clinicId=${clinicId}&clinicName=${encodeURIComponent(clinic.name)}`} className="text-primary font-medium hover:underline text-sm truncate block">{clinician.full_name}</Link>
                        <p className="text-xs text-gray-500">ID: {clinician.kipu_clinician_id}{!isActive && <span className="ml-2 font-medium text-gray-500 bg-gray-100 rounded-full px-2 py-0.5">Inactive</span>}</p>
                      </div>
                    </div>
                    <div className="flex flex-col items-end gap-2 shrink-0">
                      <div className={`transition-opacity ${included ? '' : 'opacity-50'}`}>
                        <ScoreCell
                          score={clinician.avg_cscore}
                          breakdown={clinician.breakdown}
                          weights={weights}
                          threshold={threshold}
                          title={`${clinician.full_name} average`}
                        />
                      </div>
                      <IncludeToggle included={included} onToggle={() => handleToggleInclude(clinician)} />
                    </div>
                  </div>
                    );
                  })}
              </div>
            </>
          )}
        </div>
      )}

      {/* Monthly Data Table - shown when a chart point is clicked */}
      {view === 'overview' && selectedMonth && selectedYear && (
        <div className="bg-white rounded-xl border border-[#E6E6E6] p-4 md:p-6 mt-6">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-4">
            <h3 className="text-xl font-bold text-text">
              {MONTH_NAMES[selectedMonth]} {selectedYear}
            </h3>
            <div className="flex items-center gap-2 sm:gap-3">
              <button
                onClick={() => { setSelectedMonth(null); setSelectedYear(null); }}
                className="px-3 sm:px-4 py-2 text-xs sm:text-sm font-medium text-gray-600 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Show All Clinicians
              </button>
              <button
                onClick={handleRecalculate}
                disabled={recalculating}
                className="px-3 sm:px-4 py-2 text-xs sm:text-sm font-medium border-2 border-primary text-primary rounded-lg hover:bg-primary-10 transition-colors disabled:opacity-50"
              >
                {recalculating ? 'Recalculating...' : 'Recalculate CScore'}
              </button>
            </div>
          </div>

          {loadingRecords ? (
            <div className="py-8 text-center text-gray-400 text-sm">Loading records...</div>
          ) : monthRecords.length === 0 ? (
            <p className="text-sm text-gray-500 py-4">No records for this month</p>
          ) : (
            <>
              <MissingDataBanner key={`${selectedYear}-${selectedMonth}`} gaps={missingGaps} clinicId={clinicId} clinicName={clinic.name} />

              {/* Desktop table */}
              <table className="w-full table-fixed hidden md:table">
                <colgroup>
                  <col className="w-[28%]" />
                  <col className="w-[12%]" />
                  <col className="w-[15%]" />
                  <col className="w-[15%]" />
                  <col className="w-[15%]" />
                  <col className="w-[15%]" />
                </colgroup>
                <thead>
                  <tr className="border-b border-gray-200">
                    <th className="text-left py-3 text-sm font-semibold text-gray-700">Name</th>
                    <th className="text-left py-3 text-sm font-semibold text-gray-700">CScore</th>
                    <th className="text-left py-3 text-sm font-semibold text-gray-700">Caseload</th>
                    <th className="text-left py-3 text-sm font-semibold text-gray-700">Discharges</th>
                    <th className="text-left py-3 text-sm font-semibold text-gray-700">Length of Stay</th>
                    <th className="text-left py-3 text-sm font-semibold text-gray-700">Attendance</th>
                  </tr>
                </thead>
                <tbody>
                  {monthRecords.map((record) => (
                    <tr key={record.id} className="border-b border-gray-50 last:border-0">
                      <td className="py-4">
                        <div className="flex flex-wrap items-center gap-2 pr-2">
                          <Link to={`/clinicians/${record.clinician_id}?clinicId=${clinicId}&clinicName=${encodeURIComponent(clinic.name)}`} className="text-primary font-medium hover:underline text-sm">
                            {record.clinician_name || record.clinicians?.full_name}
                          </Link>
                          <MissingBadge gap={gapByRecord.get(record.id)} />
                        </div>
                      </td>
                      <td className="py-4"><RecordScore record={record} weights={weights} threshold={threshold} /></td>
                      <td className="py-4"><CaseloadCell record={record} clinicId={clinicId} onRecordUpdate={handleRecordUpdate} /></td>
                      <td className="py-4"><DischargeCell record={record} clinicId={clinicId} onRecordUpdate={handleRecordUpdate} /></td>
                      <td className="py-4"><EditableCell value={record.length_of_stay} field="length_of_stay" recordId={record.id} clinicId={clinicId} onRecordUpdate={handleRecordUpdate} /></td>
                      <td className="py-4"><AttendanceCell record={record} clinicId={clinicId} onRecordUpdate={handleRecordUpdate} /></td>
                    </tr>
                  ))}
                </tbody>
              </table>

              {/* Mobile cards */}
              <div className="md:hidden space-y-3">
                {monthRecords.map((record) => (
                  <div key={record.id} className="border border-[#E6E6E6] rounded-lg p-3">
                    <div className="flex items-center justify-between gap-2 mb-2">
                      <div className="flex flex-wrap items-center gap-2 min-w-0">
                        <Link to={`/clinicians/${record.clinician_id}?clinicId=${clinicId}&clinicName=${encodeURIComponent(clinic.name)}`} className="text-primary font-medium hover:underline text-sm">
                          {record.clinician_name || record.clinicians?.full_name}
                        </Link>
                        <MissingBadge gap={gapByRecord.get(record.id)} />
                      </div>
                      <RecordScore record={record} weights={weights} threshold={threshold} />
                    </div>
                    <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-sm">
                      <div className="flex justify-between">
                        <span className="text-gray-500">Caseload</span>
                        <CaseloadCell record={record} clinicId={clinicId} onRecordUpdate={handleRecordUpdate} />
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-500">Discharges</span>
                        <DischargeCell record={record} clinicId={clinicId} onRecordUpdate={handleRecordUpdate} />
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-500">LOS</span>
                        <EditableCell value={record.length_of_stay} field="length_of_stay" recordId={record.id} clinicId={clinicId} onRecordUpdate={handleRecordUpdate} />
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-500">Attendance</span>
                        <AttendanceCell record={record} clinicId={clinicId} onRecordUpdate={handleRecordUpdate} />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}
